import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * LlmService - Handles LLM playground, simulation, tools, and system instructions
 */
class LlmService {

  // ==================== Playground & Simulation ====================

  /**
   * Simulate chat with custom system instruction
   * @param {Object} params - Simulation parameters
   * @param {string} params.systemPrompt - Custom system instruction
   * @param {string} params.userPrompt - User message
   * @param {string} params.modelId - Model ID to use for simulation
   * @param {string} params.userId - User ID to simulate as
   * @param {string} params.connectedSystemId - Connected system ID for context
   * @returns {Promise<Object>} Simulation response
   */
  static async simulateChat({ systemPrompt, userPrompt, modelId, userId, connectedSystemId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/llm/simulate`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          systemPrompt,
          userPrompt,
          modelId,
          userId,
          connectedSystemId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in simulation:', error);
      throw error;
    }
  }

  /**
   * Send a raw prompt to the LLM (playground mode)
   * @param {Object} params - LLM parameters
   * @param {string} params.systemPrompt - System prompt
   * @param {string} params.userPrompt - User prompt
   * @param {string} params.modelId - Model ID (UUID)
   * @param {number} params.temperature - Temperature (optional, 0-1)
   * @param {number} params.maxTokens - Max tokens (optional)
   * @returns {Promise<Object>} LLM response with content and metadata
   */
  static async sendToLlmPlayground({ systemPrompt, userPrompt, modelId, temperature, maxTokens }) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/llm/playground`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          systemPrompt,
          userPrompt,
          modelId,
          temperature,
          maxTokens,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in LLM Playground:', error);
      throw error;
    }
  }

  /**
   * Get available LLM models for the playground
   * @returns {Promise<Object>} Available models and default model
   */
  static async getLlmModels() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/llm/models`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting LLM models:', error);
      throw error;
    }
  }

  // ==================== System Instructions ====================

  /**
   * Search system instructions by name
   * @param {string} query - Search query
   * @returns {Promise<Object>} Search results with instructions array
   */
  static async searchSystemInstructions(query = '') {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/llm/system-instructions/search?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching system instructions:', error);
      throw error;
    }
  }

  /**
   * Get a system instruction by ID
   * @param {string} instructionId - The instruction ID
   * @returns {Promise<Object>} System instruction with full content
   */
  static async getSystemInstruction(instructionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/llm/system-instructions/${encodeURIComponent(instructionId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting system instruction:', error);
      throw error;
    }
  }

  /**
   * Create a new system instruction
   * @param {Object} instruction - The instruction data
   * @param {string} instruction.name - Unique name for the instruction
   * @param {string} instruction.description - Description of the instruction
   * @param {string} instruction.systemDomain - Domain category (optional, defaults to 'custom')
   * @param {string} instruction.functionalContext - Functional context (optional, defaults to 'playground')
   * @param {string} instruction.systemInstruction - The actual system prompt text
   * @returns {Promise<Object>} Created instruction with id and name
   */
  static async createSystemInstruction(instruction) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/llm/system-instructions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(instruction),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating system instruction:', error);
      throw error;
    }
  }

  // ==================== Tools ====================

  /**
   * Get all available tools for the playground
   * @returns {Promise<Object>} Object with tools array and count
   */
  static async getTools() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/tools`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tools:', error);
      throw error;
    }
  }

  /**
   * Get a specific tool by ID
   * @param {string} toolId - The tool ID
   * @returns {Promise<Object>} Tool object with functions
   */
  static async getTool(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/tools/${encodeURIComponent(toolId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tool:', error);
      throw error;
    }
  }

  /**
   * Execute a tool function
   * @param {string} toolId - The tool ID
   * @param {string} functionName - The function name to execute
   * @param {Object} parameters - Function parameters
   * @param {string} connectedSystemId - Connected system ID for multi-tenant context
   * @returns {Promise<Object>} Execution result with data, status, time, etc.
   */
  static async executeToolFunction(toolId, functionName, parameters = {}, connectedSystemId = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/tools/${encodeURIComponent(toolId)}/execute`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          functionName,
          parameters,
          connectedSystemId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error executing tool function:', error);
      throw error;
    }
  }

  // ==================== API Proxy ====================

  /**
   * Proxy an API request through the backend to avoid CORS issues
   * @param {Object} params - Request parameters
   * @param {string} params.url - The target URL
   * @param {string} params.method - HTTP method (GET, POST, PUT, DELETE, etc.)
   * @param {Object} params.headers - Request headers as key-value pairs
   * @param {string} params.body - Request body (for POST, PUT, PATCH)
   * @param {string} params.warehouseId - Optional warehouse ID to override session default
   * @returns {Promise<Object>} Proxied response with status, body, headers, time
   */
  static async proxyApiRequest({ url, method, headers, body, warehouseId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/proxy`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          url,
          method,
          headers,
          body,
          warehouseId,
        }),
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          const text = await response.text();
          if (text) errorMessage = text.substring(0, 200);
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in API proxy:', error);
      throw error;
    }
  }

  // ==================== Header Configuration ====================

  /**
   * Build HTTP headers for a connected system.
   * Headers configuration comes from lookup_values (SYSTEM_HEADER category).
   *
   * @param {string} systemCode - The connected system code (e.g., 'WMS')
   * @param {Object} context - Optional context values (userId, warehouseId, etc.)
   * @returns {Promise<Object>} Response with headers map and headersString
   */
  static async buildHeaders(systemCode, context = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/headers/build?systemCode=${encodeURIComponent(systemCode)}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(context),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to build headers: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error building headers:', error);
      throw error;
    }
  }

  /**
   * Get header templates (for displaying available header configuration).
   * @returns {Promise<Array>} List of header templates
   */
  static async getHeaderTemplates() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/headers/templates`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get header templates: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting header templates:', error);
      throw error;
    }
  }

  // ==================== User Connected Systems ====================

  /**
   * Get connected systems (warehouses) the current user has access to.
   * Used by API Hound for warehouse selection.
   * @returns {Promise<Array>} List of connected systems with id, systemCode, systemName
   */
  static async getUserConnectedSystems() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/users/me/connected-systems`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get user connected systems: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user connected systems:', error);
      throw error;
    }
  }

  // ==================== Task Status ====================

  /**
   * Get all running tasks
   * @returns {Promise<Array>} Array of running task objects
   */
  static async getRunningTasks() {
    try {
      const response = await fetch(`${API_BASE_URL}/flex/tasks/running`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting running tasks:', error);
      throw error;
    }
  }

  /**
   * Get task by ID
   * @param {string} taskId - The task ID
   * @returns {Promise<Object>} Task object
   */
  static async getTask(taskId) {
    try {
      const response = await fetch(`${API_BASE_URL}/flex/tasks/${encodeURIComponent(taskId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting task:', error);
      throw error;
    }
  }

  /**
   * Cancel a running task
   * @param {string} taskId - The task ID to cancel
   * @param {string} reason - Reason for cancellation
   * @returns {Promise<Object>} Cancel result
   */
  static async cancelTask(taskId, reason = 'Cancelled by user') {
    try {
      const response = await fetch(`${API_BASE_URL}/flex/tasks/${encodeURIComponent(taskId)}/cancel`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling task:', error);
      throw error;
    }
  }

  /**
   * Get task statistics
   * @returns {Promise<Object>} Task stats (total, running, completed, failed, cancelled)
   */
  static async getTaskStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/flex/tasks/stats`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting task stats:', error);
      throw error;
    }
  }

  // ==================== Deferred Decisions ====================

  /**
   * Get count of pending deferred decisions for notification badge
   * @param {string} connectedSystemId - The connected system ID
   * @returns {Promise<Object>} Object with count and connectedSystemId
   */
  static async getDeferredDecisionsCount(connectedSystemId) {
    try {
      const response = await fetch(`${API_BASE_URL}/deferred-decisions/pending/count?connectedSystemId=${encodeURIComponent(connectedSystemId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting deferred decisions count:', error);
      throw error;
    }
  }

  /**
   * Get all pending deferred decisions
   * @param {string} connectedSystemId - The connected system ID
   * @returns {Promise<Array>} Array of deferred decision objects
   */
  static async getPendingDeferredDecisions(connectedSystemId) {
    try {
      const response = await fetch(`${API_BASE_URL}/deferred-decisions/pending?connectedSystemId=${encodeURIComponent(connectedSystemId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting pending deferred decisions:', error);
      throw error;
    }
  }

  /**
   * Get reminders for a specific wizard
   * @param {string} wizardKey - The wizard key
   * @returns {Promise<Array>} Array of deferred decision objects relevant to the wizard
   */
  static async getWizardReminders(wizardKey) {
    try {
      const response = await fetch(`${API_BASE_URL}/deferred-decisions/reminders/${encodeURIComponent(wizardKey)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting wizard reminders:', error);
      throw error;
    }
  }

  /**
   * Resolve a deferred decision
   * @param {string} decisionId - The decision ID
   * @param {string} resolutionNotes - Optional notes about the resolution
   * @returns {Promise<Object>} Updated decision object
   */
  static async resolveDeferredDecision(decisionId, resolutionNotes = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/deferred-decisions/${encodeURIComponent(decisionId)}/resolve`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ resolutionNotes }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resolving deferred decision:', error);
      throw error;
    }
  }

  /**
   * Dismiss a deferred decision
   * @param {string} decisionId - The decision ID
   * @returns {Promise<Object>} Updated decision object
   */
  static async dismissDeferredDecision(decisionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/deferred-decisions/${encodeURIComponent(decisionId)}/dismiss`, {
        method: 'POST',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error dismissing deferred decision:', error);
      throw error;
    }
  }
}

export default LlmService;
